import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field as BaseField } from "rc-field-form";
import { cloneElement, isValidElement, useMemo } from "react";
import { FormGroup, Label, UncontrolledTooltip } from "reactstrap";
import ErrorMessage from "./ErrorMessage";

const mergeProps = (base, next) => {
	return Object.entries(next).reduce((total, [key, value]) => {
		if (!(key in total)) {
			return { ...total, [key]: value };
		}
		switch (typeof value) {
			case "function": {
				return {
					...total,
					[key]: (...args) => {
						const baseEquivalent = base[key];
						if (typeof baseEquivalent === "function") {
							baseEquivalent(...args);
						}
						value(...args);
					},
				};
			}
			default: {
				return { ...total, [key]: value };
			}
		}
	}, base);
};

function Field({ className, name, label, info, children, hidden, ...props }) {
	if (typeof children !== "function" && !isValidElement(children)) {
		throw new Error("'Field' accepts only 1 child");
	}

	const required = useMemo(() => {
		if (!props.rules) {
			return false;
		}
		return props.rules.find(({ required }) => required);
	}, [props.rules]);

	const infoId = useMemo(() => {
		if (!info || !name) {
			return;
		}
		return `${name}-info`;
	}, [info, name]);

	return (
		<FormGroup className={className} hidden={hidden}>
			{(label && !hidden) && (
				<Label for={name}>
					<span>{label}{" "}</span>
					<small className="text-red">{required && "*"}</small>
					{(info && infoId) && (
						<span className="ml-1">
							<FontAwesomeIcon
								icon={faInfoCircle}
								className="cursor-pointer"
								id={infoId}
							/>
							<UncontrolledTooltip target={infoId}>
								{info}
							</UncontrolledTooltip>
						</span>
					)}
				</Label>
			)}

			<BaseField name={name} {...props} hidden={hidden}>
				{(controll, form) => (
					<>
						{typeof children === "function"
							? (
								children(controll, form)
							)
							: (
								cloneElement(
									children,
									mergeProps(children.props, {
										id: name,
										name,
										...controll,
										hasError: form.errors.length > 0,
									})
								)
							)}

						{form.errors.length > 0 && (
							<ErrorMessage>{form.errors}</ErrorMessage>
						)}
					</>
				)}
			</BaseField>
		</FormGroup>
	);
}

export default Field;
